import React, { useEffect, useState } from 'react';
import { useParams, useLocation, useNavigate, Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { CheckCircle, XCircle, MinusCircle, Home, FileText, Loader2, Bookmark, BookmarkCheck } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TestReview = () => {
  const { testId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [test, setTest] = useState(null);
  const [submission, setSubmission] = useState(location.state?.result || null);
  const [loading, setLoading] = useState(!submission);
  const [bookmarkedIds, setBookmarkedIds] = useState(new Set());
  const [bookmarkBusy, setBookmarkBusy] = useState({});

  useEffect(() => {
    const token = localStorage.getItem('studentToken');
    if (!token) {
      navigate('/student/login');
      return;
    }
    
    if (!submission) {
      fetchSubmissionDetails();
    } else {
      fetchTest();
      fetchBookmarks();
    }
  }, []);

  const fetchBookmarks = async () => {
    try {
      const token = localStorage.getItem('studentToken');
      const res = await fetch(`${API_URL}/api/bookmarks?test_id=${testId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setBookmarkedIds(new Set(data.map(b => b.question_id)));
      }
    } catch (e) { console.error(e); }
  };

  const toggleBookmark = async (questionId) => {
    if (bookmarkBusy[questionId]) return;
    setBookmarkBusy(prev => ({ ...prev, [questionId]: true }));
    const token = localStorage.getItem('studentToken');
    const wasBookmarked = bookmarkedIds.has(questionId);
    try {
      if (wasBookmarked) {
        const res = await fetch(`${API_URL}/api/bookmarks/by-question/${testId}/${questionId}`, {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) {
          setBookmarkedIds(prev => {
            const next = new Set(prev);
            next.delete(questionId);
            return next;
          });
        }
      } else {
        const res = await fetch(`${API_URL}/api/bookmarks`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({ test_id: testId, question_id: questionId })
        });
        if (res.ok) {
          setBookmarkedIds(prev => new Set(prev).add(questionId));
        }
      }
    } catch (e) {
      console.error(e);
    } finally {
      setBookmarkBusy(prev => ({ ...prev, [questionId]: false }));
    }
  };

  const fetchTest = async () => {
    try {
      const token = localStorage.getItem('studentToken');
      const response = await fetch(`${API_URL}/api/tests/${testId}/review`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setTest(data);
      setLoading(false);
    } catch (error) {
      console.error('Error:', error);
      alert('Failed to load review. Please ensure you have completed this test.');
      navigate('/student/tests');
      setLoading(false);
    }
  };

  const fetchSubmissionDetails = async () => {
    // Future: Fetch from /api/tests/submissions/:id endpoint
    setLoading(false);
    navigate('/student/tests');
  };

  const getOptionLabel = (index) => {
    return String.fromCharCode(65 + index); // A, B, C, D
  };

  const getStudentAnswer = (questionIndex) => {
    // submission.answers is an array where index matches question index
    if (!submission?.answers || !Array.isArray(submission.answers)) {
      return undefined;
    }
    return submission.answers[questionIndex];
  };

  const isCorrect = (questionIndex, correctAnswer) => {
    const studentAnswer = getStudentAnswer(questionIndex);
    if (studentAnswer === null || studentAnswer === undefined) {
      return false;
    }
    return studentAnswer === correctAnswer;
  };

  const isAttempted = (questionIndex) => {
    const answer = getStudentAnswer(questionIndex);
    return answer !== null && answer !== undefined;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="animate-spin text-red-600" size={48} />
      </div>
    );
  }

  if (!test || !submission) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <p className="text-gray-600">No review data available</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      <section className="py-8">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            {/* Header */}
            <Card className="mb-6 bg-red-50">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h1 className="text-2xl font-bold text-gray-900 mb-2">{test.title}</h1>
                    <p className="text-gray-600">Review your answers and learn from mistakes</p>
                  </div>
                  <div className="text-center">
                    <p className="text-4xl font-bold text-red-600">{submission.score}</p>
                    <p className="text-sm text-gray-600">Your Score</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Summary Cards */}
            <div className="grid grid-cols-3 gap-4 mb-6">
              <Card className="bg-green-50 border-green-200">
                <CardContent className="p-4 text-center">
                  <CheckCircle className="mx-auto text-green-600 mb-2" size={32} />
                  <p className="text-2xl font-bold text-green-700">{submission.correct_answers}</p>
                  <p className="text-xs text-gray-600">Correct</p>
                </CardContent>
              </Card>
              
              <Card className="bg-red-50 border-red-200">
                <CardContent className="p-4 text-center">
                  <XCircle className="mx-auto text-red-600 mb-2" size={32} />
                  <p className="text-2xl font-bold text-red-700">{submission.wrong_answers}</p>
                  <p className="text-xs text-gray-600">Wrong</p>
                </CardContent>
              </Card>
              
              <Card className="bg-gray-50 border-gray-200">
                <CardContent className="p-4 text-center">
                  <MinusCircle className="mx-auto text-gray-600 mb-2" size={32} />
                  <p className="text-2xl font-bold text-gray-700">{submission.unattempted}</p>
                  <p className="text-xs text-gray-600">Skipped</p>
                </CardContent>
              </Card>
            </div>

            {/* Question-by-Question Review */}
            <div className="space-y-6">
              {test.questions.map((question, qIdx) => {
                const studentAnswer = getStudentAnswer(qIdx);
                const attempted = isAttempted(qIdx);
                const correct = isCorrect(qIdx, question.correct_answer);
                
                return (
                  <Card 
                    key={question.id} 
                    className={`border-l-4 ${
                      !attempted 
                        ? 'border-l-gray-400' 
                        : correct 
                        ? 'border-l-green-500' 
                        : 'border-l-red-500'
                    }`}
                  >
                    <CardHeader className="bg-gray-50">
                      <div className="flex items-start justify-between">
                        <CardTitle className="text-base flex items-center">
                          <span className="w-8 h-8 rounded-full bg-red-600 text-white flex items-center justify-center mr-3 text-sm font-bold">
                            {qIdx + 1}
                          </span>
                          {question.question_text}
                        </CardTitle>
                        <div className="flex items-center space-x-2">
                          {!attempted ? (
                            <span className="px-3 py-1 bg-gray-200 text-gray-700 rounded-full text-xs font-medium">
                              Not Attempted
                            </span>
                          ) : correct ? (
                            <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium flex items-center">
                              <CheckCircle className="mr-1" size={14} />
                              Correct
                            </span>
                          ) : (
                            <span className="px-3 py-1 bg-red-100 text-red-700 rounded-full text-xs font-medium flex items-center">
                              <XCircle className="mr-1" size={14} />
                              Wrong
                            </span>
                          )}
                          <button
                            data-testid={`bookmark-btn-${question.id}`}
                            onClick={() => toggleBookmark(question.id)}
                            disabled={!!bookmarkBusy[question.id]}
                            title={bookmarkedIds.has(question.id) ? 'Remove bookmark' : 'Bookmark for revision'}
                            className={`p-2 rounded-full border transition-colors ${
                              bookmarkedIds.has(question.id)
                                ? 'bg-amber-100 border-amber-400 text-amber-700'
                                : 'bg-white border-gray-300 text-gray-500 hover:bg-amber-50 hover:text-amber-600'
                            }`}
                          >
                            {bookmarkedIds.has(question.id)
                              ? <BookmarkCheck size={16} />
                              : <Bookmark size={16} />}
                          </button>
                        </div>
                      </div>
                    </CardHeader>
                    
                    <CardContent className="p-6 space-y-4">
                      {/* Options */}
                      <div className="space-y-3">
                        {question.options.map((option, optIdx) => {
                          const isCorrectOption = optIdx === question.correct_answer;
                          const isStudentAnswer = studentAnswer === optIdx;
                          
                          return (
                            <div
                              key={optIdx}
                              className={`p-4 rounded-lg border-2 ${
                                isCorrectOption
                                  ? 'bg-green-50 border-green-500'
                                  : isStudentAnswer && !isCorrectOption
                                  ? 'bg-red-50 border-red-500'
                                  : 'bg-white border-gray-200'
                              }`}
                            >
                              <div className="flex items-center">
                                <span className={`w-8 h-8 rounded-full border-2 flex items-center justify-center mr-3 font-bold text-sm ${
                                  isCorrectOption
                                    ? 'border-green-600 bg-green-600 text-white'
                                    : isStudentAnswer
                                    ? 'border-red-600 bg-red-600 text-white'
                                    : 'border-gray-400 text-gray-600'
                                }`}>
                                  {getOptionLabel(optIdx)}
                                </span>
                                <span className="flex-1">{option}</span>
                                {isCorrectOption && (
                                  <CheckCircle className="text-green-600" size={20} />
                                )}
                                {isStudentAnswer && !isCorrectOption && (
                                  <XCircle className="text-red-600" size={20} />
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>

                      {/* Explanation */}
                      {question.explanation && (
                        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                          <p className="text-sm font-semibold text-red-900 mb-2">💡 Explanation:</p>
                          <p className="text-sm text-gray-700 leading-relaxed">{question.explanation}</p>
                        </div>
                      )}

                      {/* Score Impact */}
                      <div className="flex items-center justify-between pt-3 border-t text-sm">
                        <span className="text-gray-600">Score Impact:</span>
                        <span className={`font-bold ${
                          !attempted 
                            ? 'text-gray-600' 
                            : correct 
                            ? 'text-green-600' 
                            : 'text-red-600'
                        }`}>
                          {!attempted 
                            ? '0 marks' 
                            : correct 
                            ? `+${question.marks} marks` 
                            : `-${question.negative_marks} marks`}
                        </span>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            {/* Bottom Actions */}
            <div className="grid md:grid-cols-2 gap-4 mt-8">
              <Link to="/student/tests">
                <Button className="w-full bg-red-600 hover:bg-red-700" size="lg">
                  <FileText className="mr-2" size={18} />
                  Take More Tests
                </Button>
              </Link>
              
              <Link to="/student/dashboard">
                <Button variant="outline" className="w-full" size="lg">
                  <Home className="mr-2" size={18} />
                  Back to Dashboard
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default TestReview;
